import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import { GamePhase, Judgment } from './types';
import { debugLog } from './utils/logger';
import { playSelectSound, pauseBackgroundMusic, resumeBackgroundMusic } from './utils/sound';
import HealthDisplay from './components/HealthDisplay';
import BattleArea from './components/BattleArea';
import MessageBoard from './components/MessageBoard';
import InputArea from './components/InputArea';
import TTSControls from './components/TTSControls';
import TitleScreen from './components/TitleScreen';
import TTSToggle from './components/TTSToggle';
import HowToPlay from './components/HowToPlay';
import { generateInsult, generateComeback, judgeInsultBattle, setGameDifficulty } from './components/GameAPI';
import { TTSProvider } from './context/TTSContext';
import { TTSCacheProvider } from './context/TTSCacheContext';

function AppContent() {
  // State for HowToPlay guide visibility
  const [showHowToPlay, setShowHowToPlay] = useState<boolean>(false);
  const [showHowToPlayTitle, setShowHowToPlayTitle] = useState<boolean>(false);
  // State for background music
  const [musicPaused, setMusicPaused] = useState<boolean>(true);
  // State for game difficulty
  const [difficulty, setDifficulty] = useState<string>('easy');

  debugLog('App component initialized');

  // Toggle background music play/pause
  const toggleBackgroundMusic = () => {
    if (musicPaused) {
      resumeBackgroundMusic();
    } else {
      pauseBackgroundMusic();
    }
    setMusicPaused(!musicPaused);
    playSelectSound(); // Play UI sound effect for button click
  };

  // Initialize or refresh the audio when the component mounts
  useEffect(() => {
    // This helps ensure volume controls work on iOS Safari
    document.addEventListener('click', () => {
      if (!musicPaused) {
        resumeBackgroundMusic();
      }
    }, { once: true });
  }, [musicPaused]);


  // Function to insert text at cursor or append to the end
  const insertTextAtCursor = (text: string) => {
    // Simply append the text for now
    setPlayerInput(prev => prev + text + " ");
  };

  // Game state management
  const [gamePhase, setGamePhase] = useState<GamePhase>('titleScreen');
  const [aiInsult, setAiInsult] = useState<string>('');
  const [playerResponse, setPlayerResponse] = useState<string>('');
  const [aiResponse, setAiResponse] = useState<string>('');
  const [judgment, setJudgment] = useState<Judgment>({ winner: '', explanation: '' });
  const [playerInput, setPlayerInput] = useState<string>('');
  const [playerInsult, setPlayerInsult] = useState<string>('');
  const [isPlayerTurn, setIsPlayerTurn] = useState<boolean>(false);
  const [round, setRound] = useState<number>(1);
  const [playerInitiated, setPlayerInitiated] = useState<boolean>(false);
  const [showNextRoundButton, setShowNextRoundButton] = useState<boolean>(false);
  const [swordClash, setSwordClash] = useState<boolean>(false);
  const [attackingCharacter, setAttackingCharacter] = useState<'player' | 'ai' | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [playerHealth, setPlayerHealth] = useState<number>(3);
  const [aiHealth, setAiHealth] = useState<number>(3);
  const [damagedCharacter, setDamagedCharacter] = useState<'player' | 'ai' | null>(null);
  const [judgmentDisplayed, setJudgmentDisplayed] = useState<boolean>(false);
  const [deathAnimationCompleted, setDeathAnimationCompleted] = useState<boolean>(false);

  const messageContainerRef = useRef<HTMLDivElement>(null);

  // We don't need to manually scroll the container anymore
  // as we've changed the structure to allow natural page scrolling

  // Comment out the previous scroll logic to prevent interference
  /*
  const scrollToBottom = () => {
    if (messageContainerRef.current) {
      setTimeout(() => {
        if (messageContainerRef.current) {
          messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [aiInsult, playerResponse, aiResponse, judgment]);
  */

  // Handle starting the game from title screen
  const handleStartGame = (selectedDifficulty: string) => {
    // Set the difficulty
    setDifficulty(selectedDifficulty);
    // Set the difficulty in the API
    setGameDifficulty(selectedDifficulty);

    setGamePhase('aiInsult');
    // Always ensure first round has AI going first
    setPlayerInitiated(false);
    startNewRound(false);

    // Make sure music continues playing when transitioning from title screen
    if (!musicPaused) {
      // Short delay to let audio context initialize properly
      setTimeout(() => {
        resumeBackgroundMusic();
      }, 100);
    }
  };

  // We no longer need this useEffect since handleStartGame properly starts the game
  // This prevents duplicate initialization
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPlayerInput(e.target.value);
  };

  // Handle API errors, especially rate limiting
  const handleApiError = (error: any, operation: string) => {
    debugLog(`API error in ${operation}`, error);
    setIsLoading(false);

    // Check if this is our custom ApiError type with rate limit information
    if (error && typeof error === 'object' && 'isRateLimit' in error) {
      if (error.isRateLimit) {
        setRateLimitError(`Rate limit reached: ${error.message}`);
        // Clear rate limit error after 5 seconds
        setTimeout(() => setRateLimitError(null), 5000);
      } else {
        setError(`Error: ${error.message}`);
        setTimeout(() => setError(null), 5000);
      }
    } else {
      // Handle regular errors
      const message = error instanceof Error ? error.message : 'Unknown error';
      setError(`Error: ${message}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle death animation completion
  const handleDeathAnimationComplete = useCallback((character: 'player' | 'ai') => {
    console.log(`Death animation completed for ${character}`);
    setDeathAnimationCompleted(true);
  }, []);

  // Reset death animation state when starting a new round
  useEffect(() => {
    if (gamePhase === 'aiInsult' || gamePhase === 'playerInsult') {
      setDeathAnimationCompleted(false);
    }
  }, [gamePhase]);

  // Effect to handle damage animation after judgment is displayed by user clicking "See Results"
  useEffect(() => {
    if (judgmentDisplayed && judgment.winner) {
      // Set damaged character when judgment is displayed and there's a winner
      if (playerInitiated) {
        // Player initiated the insult
        if (judgment.winner === 'comeback') {
          setDamagedCharacter('player');
        } else if (judgment.winner === 'insult') {
          setDamagedCharacter('ai');
        }
      } else {
        // AI initiated the insult
        if (judgment.winner === 'insult') {
          setDamagedCharacter('player');
        } else if (judgment.winner === 'comeback') {
          setDamagedCharacter('ai');
        }
      }

      // Show next round button after a longer delay (if animation completion doesn't fire)
      // This is a fallback timeout in case the animation completion callback doesn't trigger
      const timeoutId = setTimeout(() => {
        const isPlayerDead = playerHealth <= 0;
        const isAiDead = aiHealth <= 0;

        if (isPlayerDead || isAiDead) {
          // Update gamePhase to 'gameOver' but we won't show a dialog
          setGamePhase('gameOver');
          // Game over will be displayed in the judgment message
        } else {
          setShowNextRoundButton(true);
        }
      }, 3000); // Increase from 1500ms to 3000ms to ensure animation completes

      return () => clearTimeout(timeoutId);
    }
  }, [judgmentDisplayed, judgment.winner, playerInitiated, playerHealth, aiHealth]);

  // Show next round button when death animation completes
  useEffect(() => {
    if (deathAnimationCompleted && damagedCharacter) {
      const isPlayerDead = playerHealth <= 0;
      const isAiDead = aiHealth <= 0;

      if (isPlayerDead || isAiDead) {
        // Update gamePhase to 'gameOver'
        setGamePhase('gameOver');
      } else {
        setShowNextRoundButton(true);
      }
    }
  }, [deathAnimationCompleted, damagedCharacter, playerHealth, aiHealth]);

  // Handle player comeback submission when AI initiated the round
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!playerInput.trim()) return;

    // First set the player's response, then trigger the animation
    setPlayerResponse(playerInput);
    // Trigger the sword clash animation for player's comeback
    setSwordClash(true);
    setAttackingCharacter('player');
    setTimeout(() => {
      setSwordClash(false);
      setAttackingCharacter(null);
    }, 800);

    setGamePhase('judgment');
    setIsPlayerTurn(false);
    setIsLoading(true);

    judgeInsultBattle(aiInsult, playerInput)
      .then(result => {
        setJudgment(result);
        const newPlayerHealth = result.winner === 'insult' ? playerHealth - 1 : playerHealth;
        const newAiHealth = result.winner === 'comeback' ? aiHealth - 1 : aiHealth;

        setPlayerHealth(Math.max(0, newPlayerHealth));
        setAiHealth(Math.max(0, newAiHealth));
        setIsLoading(false);
        setJudgmentDisplayed(false); // Reset judgment displayed state

        // Wait for user to click "See Results" before showing effects
        // Death animation will be triggered by the useEffect above
      })
      .catch(err => {
        handleApiError(err, 'judgeInsultBattle');
        // Revert game state if there's an error
        setGamePhase('playerResponse');
        setIsPlayerTurn(true);
        setPlayerResponse('');
      });

    setPlayerInput('');
  };

  const startNewRound = (isPlayerInit?: boolean) => {
    debugLog('Starting new round', { isPlayerInit });
    setShowNextRoundButton(false);
    setAiInsult('');
    setPlayerInsult('');
    setPlayerResponse('');
    setAiResponse('');
    setJudgment({ winner: '', explanation: '' });
    setError(null);
    setRateLimitError(null);
    setDamagedCharacter(null);
    setJudgmentDisplayed(false);

    // Use the provided value or fallback to state
    const shouldPlayerInitiate = isPlayerInit !== undefined ? isPlayerInit : playerInitiated;
    debugLog('Should player initiate', { shouldPlayerInitiate });
    if (shouldPlayerInitiate) {
      setGamePhase('playerInsult');
      setIsPlayerTurn(true);
    } else {
      setGamePhase('aiInsult');
      setIsLoading(true);

      generateInsult()
        .then(insult => {
          setAiInsult(insult);
          // Only trigger sword clash animation after insult is ready
          setSwordClash(true);
          setAttackingCharacter('ai');
          setTimeout(() => {
            setSwordClash(false);
            setAttackingCharacter(null);
          }, 800);
          setGamePhase('playerResponse');
          setIsPlayerTurn(true);
          setIsLoading(false);
        })
        .catch(err => {
          handleApiError(err, 'generateInsult');
          // If we can't get an insult, let the player start instead
          setGamePhase('playerInsult');
          setIsPlayerTurn(true);
          setIsLoading(false);
        });
    }
  };

  const handleNextRound = () => {
    // Play select sound when next round button is clicked
    playSelectSound();

    // Calculate the new value before updating state
    const newPlayerInitiated = !playerInitiated;
    setPlayerInitiated(newPlayerInitiated);
    setRound(prev => prev + 1);
    // Pass the new value directly to startNewRound
    startNewRound(newPlayerInitiated);
  };

  const handlePlayerInsult = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!playerInput.trim()) return;

    // First set the player's insult
    setPlayerInsult(playerInput);
    // Then trigger the animation for player's attack
    setSwordClash(true);
    setAttackingCharacter('player');
    setTimeout(() => {
      setSwordClash(false);
      setAttackingCharacter(null);
    }, 800);

    setGamePhase('aiComeback');
    setIsPlayerTurn(false);
    setIsLoading(true);

    generateComeback(playerInput)
      .then(comeback => {
        setAiResponse(comeback);
        // Trigger the sword clash animation for AI's comeback
        setSwordClash(true);
        setAttackingCharacter('ai');
        setTimeout(() => {
          setSwordClash(false);
          setAttackingCharacter(null);
        }, 800);
        setGamePhase('judgment');

        judgeInsultBattle(playerInput, comeback)
          .then(result => {
            setJudgment(result);
            const newPlayerHealth = result.winner === 'comeback' ? playerHealth - 1 : playerHealth;
            const newAiHealth = result.winner === 'insult' ? aiHealth - 1 : aiHealth;

            setPlayerHealth(Math.max(0, newPlayerHealth));
            setAiHealth(Math.max(0, newAiHealth));
            setIsLoading(false);
            setJudgmentDisplayed(false); // Reset judgment displayed state

            // Wait for user to click "See Results" before showing effects
            // Death animation will be triggered by the useEffect above
          })
          .catch(err => {
            handleApiError(err, 'judgeInsultBattle');
            // Go back to player turn if we can't judge
            setGamePhase('playerInsult');
            setIsPlayerTurn(true);
            setAiResponse('');
          });
      })
      .catch(err => {
        handleApiError(err, 'generateComeback');
        // Go back to player turn if we can't get a comeback
        setGamePhase('playerInsult');
        setIsPlayerTurn(true);
      });

    setPlayerInput('');
  };

  return (
    <div className="App">
      {gamePhase === 'titleScreen' ? (
        <>
          <TitleScreen
            onPlayClick={handleStartGame}
            onHowToPlayClick={() => setShowHowToPlayTitle(true)}
          />
          <HowToPlay
            isOpen={showHowToPlayTitle}
            onClose={() => setShowHowToPlayTitle(false)}
          />
        </>
      ) : (
        <>
          <h1 className="game-title title">Pirate Insult Swordfight Simulator</h1>
          <div className="game-controls" style={{ position: 'absolute', top: '10px', right: '10px' }}>
          </div>
          <HowToPlay
            isOpen={showHowToPlay}
            onClose={() => setShowHowToPlay(false)}
          />
          {error && <div className="error-message nes-container is-rounded is-error">{error}</div>}
          {rateLimitError && <div className="rate-limit-message nes-container is-rounded is-warning">{rateLimitError}</div>}
          <HealthDisplay
            playerHealth={playerHealth}
            aiHealth={aiHealth}
            onHelpClick={() => setShowHowToPlay(prev => !prev)}
            musicPaused={musicPaused}
            onToggleMusic={toggleBackgroundMusic}
          />
          <BattleArea
            swordClash={swordClash}
            gamePhase={gamePhase}
            attackingCharacter={attackingCharacter}
            damagedCharacter={damagedCharacter}
            onDeathAnimationComplete={handleDeathAnimationComplete}
          />
          <MessageBoard
            round={round}
            playerInitiated={playerInitiated}
            playerInsult={playerInsult}
            aiInsult={aiInsult}
            playerResponse={playerResponse}
            aiResponse={aiResponse}
            judgment={judgment}
            gamePhase={gamePhase}
            isPlayerTurn={isPlayerTurn}
            isLoading={isLoading}
            playerInput={playerInput}
            handleInputChange={handleInputChange}
            handleSubmit={handleSubmit}
            handlePlayerInsult={handlePlayerInsult}
            showNextRoundButton={showNextRoundButton}
            handleNextRound={handleNextRound}
            insertTextAtCursor={insertTextAtCursor}
            judgmentDisplayed={judgmentDisplayed}
            setJudgmentDisplayed={setJudgmentDisplayed}
          />
        </>
      )}
    </div>
  );
}

function App() {
  return (
    <TTSProvider>
      <TTSCacheProvider>
        <AppContent />
      </TTSCacheProvider>
    </TTSProvider>
  );
}

export default App;
